import { useEffect, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconArrowRight } from '@iwoca/icons';
import {
  IconCalendar60m,
  IconMoneyTopup,
  IconPaymentIn,
  IconRepaymentReduction,
} from '@iwoca/icons/brand';
import { Trans } from '@lingui/react/macro';
import Rive from '@rive-app/react-canvas';
import { clsx } from 'clsx';

import styles from './CheckEligibilityDE.module.css';
import { useGetStateQuery } from '../../../api/lendingApi/lendingApiQueries';
// @ts-expect-error does not recognise riv import.
import successTick from '../../../assets/animation/successTick.riv';
import { useContextStateKey } from '../../../hooks/useContextStateKey';
import { useIsInDERepaymentReductionTest } from '../../../hooks/useIsInDERepaymentReductionTest';
import { useOffersDE } from '../../../hooks/useOffers/useOffersDE';
import { useSignupRouterDE } from '../../../hooks/useSignupRouter/useSignupRouterDE';
import { TGetStateResponse } from '../../../types';
import { getAmplitudeDefaultEventProperties } from '../../../utils/amplitudeEvents';
import ApplicationLoader from '../../ApplicationLoader/ApplicationLoader';
import { Button } from '../../Button/Button';

const CheckEligibilityDE = () => {
  const [isNavigating, setIsNavigating] = useState(false);

  const { navigateSignupDE } = useSignupRouterDE();

  const { isStateFetching } = useGetStateQuery<TGetStateResponse>();

  const stateKey = useContextStateKey();

  const offersInformation = useOffersDE();
  const documentRequirements = offersInformation.documentRequirements;

  const {
    isIncludedInRepaymentReductionTest,
    isLoading: isRepaymentReductionTestLoading,
  } = useIsInDERepaymentReductionTest();

  useEffect(() => {
    tracking.ampli.stepLoaded({
      'Step name': 'Eligibility check',
      ...getAmplitudeDefaultEventProperties(),
    });
  }, []);

  if (
    offersInformation.isLoadingOrRedirecting ||
    isRepaymentReductionTestLoading
  ) {
    return (
      <ApplicationLoader
        showMarketingPoll={offersInformation.isInExitPollTestDE}
      />
    );
  }

  if (!documentRequirements) {
    return null;
  }

  const navigateToTradingHistory = async () => {
    setIsNavigating(true);

    await navigateSignupDE('schritt-3', stateKey);

    setIsNavigating(false);
  };

  return (
    <section className={styles.checkEligibilityContainer}>
      <div className={styles.animationContainer}>
        <Rive
          src={successTick}
          className={styles.accountSuccessTickRive}
          data-testid="success-tick-animation"
        />
      </div>
      <div className={styles.titleContainer}>
        <h1 className={styles.title}>
          <Trans>Good news, you’re eligible!</Trans>
        </h1>
      </div>

      <section className={styles.benefitsCard}>
        <p className={styles.benefitsHeading}>
          <Trans>The flexibility of our loans in summary:</Trans>
        </p>
        <div
          className={clsx(styles.benefitsContainer, {
            [styles.benefitsContainerFourUp]:
              isIncludedInRepaymentReductionTest,
          })}
        >
          <div className={styles.benefit}>
            <IconCalendar60m className={styles.benefitIcon} />
            <p>
              <Trans>Loan terms of up to 60 months</Trans>
            </p>
          </div>
          <div className={styles.benefit}>
            <IconPaymentIn className={styles.benefitIcon} />
            <p>
              <Trans>Free early repayments</Trans>
            </p>
          </div>
          {isIncludedInRepaymentReductionTest && (
            <div className={styles.benefit}>
              <IconRepaymentReduction className={styles.benefitIcon} />
              <p>
                <Trans>Repayment reductions upon request</Trans>
              </p>
            </div>
          )}
          <div className={styles.benefit}>
            <IconMoneyTopup className={styles.benefitIcon} />
            <p>
              <Trans>Apply for a credit increase</Trans>
            </p>
          </div>
        </div>
      </section>
      <p className={styles.terms}>
        <Trans>You are just one step away from your offer.</Trans>
      </p>

      <Button
        className={styles.nextButton}
        disabled={isStateFetching}
        loading={isNavigating}
        type="submit"
        onClick={navigateToTradingHistory}
      >
        <Trans>Next step</Trans>
        <IconArrowRight />
      </Button>
    </section>
  );
};

export default CheckEligibilityDE;
